import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '@/shared/api/errors';

import { type QuoteListParams, quoteKeys, quotesApi } from './api';
import { waitingOnSending } from './sending';
import type {
  ListResolution,
  Quote,
  QuoteCopies,
  QuoteCreate,
  QuoteLinesWrite,
  QuoteSendWrite,
  QuotePdfLink,
  QuoteUpdate,
  QuoteWarningPatch,
} from './types';

export function useQuoteList(params: QuoteListParams) {
  return useQuery({
    queryKey: quoteKeys.list(params),
    queryFn: () => quotesApi.list(params),
  });
}

/** How often the editor asks again while a sending is on its way (contract v10.26). */
export const SENDING_POLL_MS = 3_000;

/** Whether a sending of this quote is between «Enviar» and the worker's answer, or its
 * copy in Sent is still being written. */
export function sendingInFlight(quote: Quote | undefined): boolean {
  return waitingOnSending(quote?.sendings);
}

export function useQuote(id: string | undefined) {
  return useQuery<Quote, ApiError>({
    queryKey: quoteKeys.detail(id ?? ''),
    queryFn: () => quotesApi.detail(id ?? ''),
    enabled: Boolean(id),
    // While the worker sends it, poll: the screen goes from «Enviando…» to «Enviado» or
    // to the reason, without anybody pressing F5.
    refetchInterval: (query) => (sendingInFlight(query.state.data) ? SENDING_POLL_MS : false),
  });
}

/**
 * Every write of the editor, in one hook.
 *
 * All three endpoints answer with the whole quote, recomputed, so the cache is **written
 * from the answer** and nothing is refetched: the API has just said what the totals are,
 * and asking it again would only give the screen a chance to disagree with itself for a
 * few hundred milliseconds.
 *
 * Only the **lists** are invalidated, never `quoteKeys.all`: `all` is a prefix of the detail
 * key too, so invalidating it would fire a refetch of the very quote just written and put the
 * stale answer back on screen. The lists do need it, because they show the total and the
 * status.
 */
function useQuoteMutation<TVariables>(
  id: string,
  mutationFn: (variables: TVariables) => Promise<Quote>,
) {
  const queryClient = useQueryClient();
  return useMutation<Quote, ApiError, TVariables>({
    mutationFn,
    onSuccess: (quote) => {
      queryClient.setQueryData(quoteKeys.detail(id), quote);
      void queryClient.invalidateQueries({ queryKey: quoteKeys.lists });
    },
  });
}

/**
 * Saving the lines replaces them all. A 409 here (contract v10.52) is lines added from
 * another screen while this one was open (an «Añadir» in the search, another tab): nothing
 * was saved, and the quote is asked again so the editor shows them next to what was typed,
 * ready to save once more.
 */
export function useSaveLines(id: string) {
  const queryClient = useQueryClient();
  return useMutation<Quote, ApiError, QuoteLinesWrite>({
    mutationFn: (payload) => quotesApi.putLines(id, payload),
    onSuccess: (quote) => {
      queryClient.setQueryData(quoteKeys.detail(id), quote);
      void queryClient.invalidateQueries({ queryKey: quoteKeys.lists });
    },
    onError: (error) => {
      if (error.status === 409)
        void queryClient.invalidateQueries({ queryKey: quoteKeys.detail(id) });
    },
  });
}

export function useRecalculate(id: string) {
  return useQuoteMutation<string | undefined>(id, (marginPct) =>
    quotesApi.recalculate(id, marginPct),
  );
}

export function useCancelQuote(id: string) {
  return useQuoteMutation<void>(id, () => quotesApi.cancel(id));
}

export function useReviewQuote(id: string) {
  return useQuoteMutation<void>(id, () => quotesApi.review(id));
}

export function useMarkSent(id: string) {
  return useQuoteMutation<void>(id, () => quotesApi.markSent(id));
}

export function useSendQuote(id: string) {
  return useQuoteMutation<QuoteSendWrite>(id, (payload) => quotesApi.send(id, payload));
}

export function useRetryAppend(id: string) {
  return useQuoteMutation<string>(id, (sendingId) => quotesApi.retryAppend(id, sendingId));
}

export function useUpdateQuote(id: string) {
  return useQuoteMutation<QuoteUpdate>(id, (payload) => quotesApi.update(id, payload));
}

export function useSetWarnings(id: string) {
  return useQuoteMutation<number[]>(id, (included) => quotesApi.putWarnings(id, { included }));
}

/** One supplier note rewritten (contract v10.6). `position` is the index into the API's list. */
export function useEditWarning(id: string) {
  return useQuoteMutation<{ position: number; payload: QuoteWarningPatch }>(
    id,
    ({ position, payload }) => quotesApi.patchWarning(id, position, payload),
  );
}

/**
 * The supplier's document, uploaded by hand (contract v8).
 *
 * The answer is the quote with `email_id` set and `offer_read_at` still null: queued,
 * not read. Written to the cache like every other write, and from there
 * `useOfferReading` takes over the polling until the lines arrive.
 */
export function useUploadOfferDocuments(id: string) {
  return useQuoteMutation<File[]>(id, (files) => quotesApi.uploadOfferDocuments(id, files));
}

/**
 * An empty draft, made by hand: an offer that came by phone, a quote for something that
 * was never on any supplier's document, or one to fill from the tariffs (`origin:
 * 'tariff'`, contract v10.52). The API answers with the quote; the caller goes to its
 * editor. Nothing to invalidate but the lists.
 */
export function useCreateQuote() {
  const queryClient = useQueryClient();
  return useMutation<Quote, ApiError, QuoteCreate>({
    mutationFn: (payload) => quotesApi.create(payload),
    onSuccess: (quote) => {
      queryClient.setQueryData(quoteKeys.detail(quote.id), quote);
      void queryClient.invalidateQueries({ queryKey: quoteKeys.lists });
    },
  });
}

/**
 * The signed link to the PDF, retried while the worker is still rendering.
 *
 * The API answers 409 with "el PDF se esta generando" until the object exists, which is
 * seconds after the approval. Polling on that one status, and only while the quote is not
 * a draft, is what turns "approve and then press a button that fails" into a preview that
 * simply appears. Any other error stops the retries: a 404 is not going to fix itself.
 *
 * The backoff grows and the ceiling is about a minute and a half, not fifteen seconds: the
 * render happens on a queue, and a worker busy with a batch of mail takes longer than a
 * fixed 1,5 s × 10 allows. Past that the screen offers a button rather than a dead end.
 */
export function useQuotePdf(id: string, enabled: boolean) {
  return useQuery<QuotePdfLink, ApiError>({
    queryKey: [...quoteKeys.detail(id), 'pdf'],
    queryFn: () => quotesApi.pdf(id),
    enabled,
    retry: (failureCount, error) => error.status === 409 && failureCount < 12,
    retryDelay: (failureCount) => Math.min(1500 * 2 ** failureCount, 15_000),
  });
}

/**
 * Download the draft's PDF (contract v10.18) and hand it to the browser as a file.
 *
 * A mutation and not a query: it is something the person does, every press asks again
 * (the draft may have changed since), and nothing about it belongs in the cache. The
 * bytes go through an object URL, which is same origin, so the `download` attribute is
 * honoured and the app is not navigated away into the native viewer.
 */
export function useDownloadDraftPdf(id: string) {
  return useMutation<void, ApiError>({
    mutationFn: async () => {
      const file = await quotesApi.draftPdf(id);
      saveFile(file.blob, file.filename ?? 'BORRADOR.pdf');
    },
  });
}

function saveFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked on the next tick: some browsers start the download asynchronously and a URL
  // revoked in the same tick as the click downloads nothing.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Re-enqueue the render. Used by the "Reintentar" button when the wait ran out. */
export function useRetryPdf(id: string) {
  return useQuoteMutation<void>(id, () => quotesApi.retryPdf(id));
}

/** The copies and redos of a quote, newest first: what «Para otros clientes» lists. */
export function useQuoteCopies(sourceId: string) {
  return useQuoteList({ duplicated_from_id: sourceId, limit: 50 });
}

/**
 * The same quote for other customers (contract v10.8). The API answers with one draft
 * per customer; each goes into the cache under its own id, and the lists (the copies of
 * this one among them) are invalidated.
 */
export function useCopyForClients(sourceId: string) {
  const queryClient = useQueryClient();
  return useMutation<QuoteCopies, ApiError, string[]>({
    mutationFn: (clientIds) => quotesApi.copies(sourceId, clientIds),
    onSuccess: (copies) => {
      for (const quote of copies.items) {
        queryClient.setQueryData(quoteKeys.detail(quote.id), quote);
      }
      void queryClient.invalidateQueries({ queryKey: quoteKeys.lists });
    },
  });
}

/**
 * «Buscar en las tarifas» of «Pegar lista»: the pasted text or the customer's file. A
 * mutation and not a query: it is something the person does, and its answer belongs to
 * the dialog that asked, not to a cache shared by other screens.
 */
export function useResolveList() {
  return useMutation<ListResolution, ApiError, string | File>({
    mutationFn: (source) =>
      typeof source === 'string'
        ? quotesApi.resolveList(source)
        : quotesApi.resolveListFile(source),
  });
}
